import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import {
  Timer, Play, Stop, Check, Leaf, Wind,
  PencilLine, Sparkle, ArrowRight, BookOpen,
} from "@phosphor-icons/react";
import FrequencyPlayer from "../components/FrequencyPlayer";
import PremiumUpgrade, { PremiumBadge } from "../components/PremiumUpgrade";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = [5, 10, 15];

const SESSION_FREQUENCIES = [
  { hz: 432, label: "432 Hz - Harmonic calm & universal healing", color: "#84A98C" },
  { hz: 528, label: "528 Hz - Transformation & DNA repair", color: "#F4A261" },
  { hz: 396, label: "396 Hz - Liberation from fear & guilt", color: "#2D6A4F" },
  { hz: 741, label: "741 Hz - Intuition & self-expression", color: "#CDB4DB" },
];

const PHASES = [
  { label: "Breathe in", duration: 4 },
  { label: "Hold", duration: 4 },
  { label: "Breathe out", duration: 6 },
  { label: "Rest", duration: 2 },
];

const JOURNAL_PROMPTS = [
  "What sensations did you notice in your body during the session?",
  "What thoughts surfaced that you'd like to release?",
  "What are you most grateful for in this moment?",
  "How does your inner landscape feel different now compared to before?",
  "If your calm had a color and shape, what would it look like?",
  "What is one intention you'd like to carry with you from this reset?",
];

const MOOD_OPTIONS = [
  { id: "calm", label: "Calm", color: "#84A98C" },
  { id: "peaceful", label: "Peaceful", color: "#2D6A4F" },
  { id: "refreshed", label: "Refreshed", color: "#F4A261" },
  { id: "grounded", label: "Grounded", color: "#457B9D" },
  { id: "hopeful", label: "Hopeful", color: "#FFD166" },
  { id: "lighter", label: "Lighter", color: "#CDB4DB" },
  { id: "same", label: "About the same", color: "#78857F" },
  { id: "restless", label: "Still restless", color: "#E07A5F" },
];

export default function SensoryResetPage() {
  const { user } = useAuth();
  const isPremium = user?.is_premium;
  const [stage, setStage] = useState("select"); // select | active | complete | journal | saved
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(10);
  const [sessionId, setSessionId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseTime, setPhaseTime] = useState(0);
  const intervalRef = useRef(null);
  const [sessions, setSessions] = useState([]);
  const [selectedFreq, setSelectedFreq] = useState(SESSION_FREQUENCIES[0]);

  // Journal state
  const [journalPrompt, setJournalPrompt] = useState("");
  const [journalEntry, setJournalEntry] = useState("");
  const [moodAfter, setMoodAfter] = useState("");
  const [savingJournal, setSavingJournal] = useState(false);
  const [savedJournal, setSavedJournal] = useState(null);
  const [pastJournals, setPastJournals] = useState([]);
  const [showPastJournals, setShowPastJournals] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sessions/history`, { withCredentials: true })
      .then((res) => setSessions(res.data))
      .catch(() => {});
  }, [stage]);

  const startSession = async () => {
    try {
      const res = await axios.post(
        `${API}/sessions/start`,
        { duration: selectedDuration },
        { withCredentials: true }
      );
      setSessionId(res.data.id);
      setTimeLeft(selectedDuration * 60);
      setPhaseIndex(0);
      setPhaseTime(0);
      setStage("active");
    } catch (err) {
      console.error("Failed to start session:", err);
    }
  };

  const completeSession = useCallback(async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (sessionId) {
      try {
        await axios.post(`${API}/sessions/${sessionId}/complete`, {}, { withCredentials: true });
      } catch {}
    }
    setStage("complete");
  }, [sessionId]);

  useEffect(() => {
    if (stage !== "active") return;
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          completeSession();
          return 0;
        }
        return prev - 1;
      });
      setPhaseTime((prev) => {
        const currentPhase = PHASES[phaseIndex];
        if (prev >= currentPhase.duration - 1) {
          setPhaseIndex((pi) => (pi + 1) % PHASES.length);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [stage, phaseIndex, completeSession]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progress = stage === "active"
    ? 1 - timeLeft / (selectedDuration * 60)
    : 0;

  const currentPhase = PHASES[phaseIndex];
  const phaseProgress = phaseTime / currentPhase.duration;
  const breathScale = currentPhase.label === "Breathe in"
    ? 1 + phaseProgress * 0.4
    : currentPhase.label === "Breathe out"
    ? 1.4 - phaseProgress * 0.4
    : currentPhase.label === "Hold"
    ? 1.4
    : 1;

  const completedCount = sessions.filter((s) => s.completed).length;
  const totalMinutes = sessions.filter((s) => s.completed).reduce((acc, s) => acc + s.duration, 0);

  // Journal functions
  const openJournal = () => {
    const prompt = JOURNAL_PROMPTS[Math.floor(Math.random() * JOURNAL_PROMPTS.length)];
    setJournalPrompt(prompt);
    setJournalEntry("");
    setMoodAfter("");
    setSavedJournal(null);
    setStage("journal");
  };

  const saveJournal = async () => {
    if (!journalEntry.trim()) return;
    setSavingJournal(true);
    try {
      const res = await axios.post(
        `${API}/sessions/journal`,
        {
          session_id: sessionId || "standalone",
          prompt: journalPrompt,
          entry: journalEntry.trim(),
          mood_before: null,
          mood_after: moodAfter || null,
        },
        { withCredentials: true }
      );
      setSavedJournal(res.data);
      setStage("saved");
    } catch (err) {
      console.error("Failed to save journal:", err);
    } finally {
      setSavingJournal(false);
    }
  };

  const loadPastJournals = async () => {
    try {
      const res = await axios.get(`${API}/sessions/journals`, { withCredentials: true });
      setPastJournals(res.data);
      setShowPastJournals(true);
    } catch {}
  };

  const resetAll = () => {
    setStage("select");
    setSessionId(null);
    setJournalEntry("");
    setMoodAfter("");
    setSavedJournal(null);
    setShowPastJournals(false);
  };

  return (
    <div data-testid="sensory-reset-page">
      <AnimatePresence mode="wait">
        {/* ── Select Duration ── */}
        {stage === "select" && (
          <motion.div
            key="select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">Sensory Reset</p>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
                Choose Your Session
              </h1>
              <p className="text-sm text-[#78857F] mt-2">
                Immerse yourself in a guided breathing experience.
              </p>
            </div>

            {/* Duration Cards */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {DURATIONS.map((d) => (
                <motion.button
                  key={d}
                  data-testid={`duration-${d}min`}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setSelectedDuration(d)}
                  className={`card-neuro p-6 text-center transition-all duration-300 ${
                    selectedDuration === d
                      ? "ring-2 ring-[#2D6A4F] shadow-lg"
                      : ""
                  }`}
                >
                  <Timer size={28} weight="duotone" className="text-[#2D6A4F] mx-auto mb-2" />
                  <p className="font-['Outfit'] text-2xl font-medium text-[#1A2421]">{d}</p>
                  <p className="text-xs text-[#78857F]">minutes</p>
                </motion.button>
              ))}
            </div>

            {/* Frequency Selector */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#1A2421] mb-3">Sound Frequency</p>
              <div className="grid grid-cols-2 gap-3">
                {SESSION_FREQUENCIES.map((freq) => (
                  <button
                    key={freq.hz}
                    data-testid={`freq-select-${freq.hz}`}
                    onClick={() => setSelectedFreq(freq)}
                    className={`card-neuro p-3 text-left transition-all duration-300 ${
                      selectedFreq.hz === freq.hz ? "ring-2 shadow-md" : ""
                    }`}
                    style={selectedFreq.hz === freq.hz ? { borderColor: freq.color } : {}}
                  >
                    <p className="font-['Outfit'] text-sm font-medium text-[#1A2421]">{freq.hz} Hz</p>
                    <p className="text-[10px] text-[#78857F] leading-tight mt-0.5">
                      {freq.label.replace(/^\d+\s*Hz\s*[-–—]\s*/, "")}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={startSession}
              data-testid="session-start-button"
              className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium transition-all hover:-translate-y-0.5 shadow-md"
            >
              <Play size={20} weight="fill" className="mr-2" />
              Begin {selectedDuration}-Minute Reset
            </Button>

            {/* Past Sessions & Journal Link */}
            <div className="mt-10 flex flex-col gap-6">
              {sessions.length > 0 && (
                <div>
                  <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-4">Your Practice</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="card-neuro p-4 text-center">
                      <p className="font-['Outfit'] text-2xl font-medium text-[#2D6A4F]">{completedCount}</p>
                      <p className="text-xs text-[#78857F]">Sessions Completed</p>
                    </div>
                    <div className="card-neuro p-4 text-center">
                      <p className="font-['Outfit'] text-2xl font-medium text-[#2D6A4F]">{totalMinutes}</p>
                      <p className="text-xs text-[#78857F]">Total Minutes</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Past Journals */}
              <button
                onClick={loadPastJournals}
                data-testid="view-past-journals"
                className="flex items-center gap-2 text-sm text-[#2D6A4F] font-medium hover:underline"
              >
                <BookOpen size={18} weight="duotone" />
                View Past Journals
              </button>

              {showPastJournals && pastJournals.length > 0 && (
                <div className="space-y-3" data-testid="past-journals-list">
                  {pastJournals.slice(0, 5).map((j) => (
                    <div key={j.id} className="card-neuro p-4" data-testid={`journal-${j.id}`}>
                      <p className="text-[10px] text-[#78857F] mb-1">
                        {new Date(j.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {j.mood_after && <span className="ml-2 capitalize">Felt: {j.mood_after}</span>}
                      </p>
                      <p className="text-xs text-[#84A98C] italic mb-1">"{j.prompt}"</p>
                      <p className="text-sm text-[#1A2421] leading-relaxed">{j.entry}</p>
                      {j.ai_reflection && (
                        <div className="mt-2 pt-2 border-t border-[#E1D9CD]">
                          <p className="text-xs text-[#2D6A4F] italic">{j.ai_reflection}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showPastJournals && pastJournals.length === 0 && (
                <p className="text-sm text-[#78857F]">No journal entries yet. Complete a session to start journaling.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* ── Active Session ── */}
        {stage === "active" && (
          <motion.div
            key="active"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center min-h-[70vh]"
          >
            <div className="relative mb-8">
              <motion.div
                animate={{ scale: breathScale }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                className="w-48 h-48 rounded-full flex items-center justify-center"
                style={{
                  background: "radial-gradient(circle, rgba(45,106,79,0.15) 0%, rgba(132,169,140,0.08) 70%, transparent 100%)",
                }}
                data-testid="breathing-circle"
              >
                <motion.div
                  animate={{ scale: breathScale * 0.85 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                  className="w-32 h-32 rounded-full flex items-center justify-center"
                  style={{
                    background: "radial-gradient(circle, rgba(45,106,79,0.25) 0%, rgba(45,106,79,0.1) 100%)",
                  }}
                >
                  <p className="font-['Outfit'] text-3xl font-light text-[#2D6A4F]" data-testid="session-timer">
                    {formatTime(timeLeft)}
                  </p>
                </motion.div>
              </motion.div>
              <svg className="absolute inset-0 w-48 h-48 -rotate-90" viewBox="0 0 192 192">
                <circle cx="96" cy="96" r="90" fill="none" stroke="#E1D9CD" strokeWidth="2" />
                <circle
                  cx="96" cy="96" r="90" fill="none"
                  stroke="#2D6A4F" strokeWidth="2.5" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 90}
                  strokeDashoffset={2 * Math.PI * 90 * (1 - progress)}
                  className="timer-ring"
                />
              </svg>
            </div>

            <motion.div
              key={currentPhase.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Wind size={20} weight="duotone" className="text-[#84A98C]" />
                <p className="font-['Outfit'] text-xl font-medium text-[#1A2421]" data-testid="breathing-phase-label">
                  {currentPhase.label}
                </p>
              </div>
              <p className="text-sm text-[#78857F]">
                {currentPhase.label === "Breathe in" && "Fill your lungs slowly and deeply"}
                {currentPhase.label === "Hold" && "Hold with gentle awareness"}
                {currentPhase.label === "Breathe out" && "Release slowly and completely"}
                {currentPhase.label === "Rest" && "Pause in stillness"}
              </p>
            </motion.div>

            <div className="w-full max-w-xs mb-6">
              <FrequencyPlayer
                frequencyText={selectedFreq.label}
                accentColor={selectedFreq.color}
                autoPlay={true}
                compact={true}
              />
            </div>

            <Button
              onClick={completeSession}
              data-testid="session-end-button"
              variant="outline"
              className="rounded-full border-[#E1D9CD] text-[#78857F] hover:text-[#E76F51] hover:border-[#E76F51]"
            >
              <Stop size={18} weight="fill" className="mr-2" />
              End Session
            </Button>
          </motion.div>
        )}

        {/* ── Complete (transition screen) ── */}
        {stage === "complete" && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center min-h-[70vh] text-center"
            data-testid="session-complete"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-20 h-20 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mb-6"
            >
              <Check size={36} weight="bold" className="text-[#2D6A4F]" />
            </motion.div>

            <h2 className="font-['Outfit'] text-2xl font-medium text-[#1A2421] mb-2">Session Complete</h2>
            <p className="text-sm text-[#78857F] mb-2">
              You completed a {selectedDuration}-minute sensory reset.
            </p>
            <div className="flex items-center gap-2 mb-8">
              <Leaf size={16} weight="duotone" className="text-[#84A98C]" />
              <p className="text-sm text-[#84A98C] italic">"Carry this calm with you."</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={openJournal}
                data-testid="open-journal-button"
                className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white px-8 transition-all hover:-translate-y-0.5 shadow-md"
              >
                <PencilLine size={18} weight="duotone" className="mr-2" />
                Write a Reflection
              </Button>
              <Button
                onClick={resetAll}
                data-testid="skip-journal-button"
                variant="outline"
                className="rounded-full border-[#E1D9CD] text-[#78857F] px-8"
              >
                Skip for now
              </Button>
            </div>
          </motion.div>
        )}

        {/* ── Journal Entry ── */}
        {stage === "journal" && (
          <motion.div
            key="journal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-xl mx-auto"
            data-testid="session-journal"
          >
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">Session Journal</p>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2421] tracking-tight">
                Capture This Moment
              </h2>
              <p className="text-sm text-[#78857F] mt-1">
                While the calm is still fresh, take a moment to reflect.
              </p>
            </div>

            {/* Journaling Prompt */}
            <div className="card-neuro p-6 mb-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#84A98C]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkle size={18} weight="duotone" className="text-[#84A98C]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#78857F] font-semibold mb-1">Reflection Prompt</p>
                  <p className="text-sm text-[#1A2421] italic leading-relaxed" data-testid="journal-prompt-text">
                    "{journalPrompt}"
                  </p>
                </div>
              </div>
              <button
                onClick={() => setJournalPrompt(JOURNAL_PROMPTS[Math.floor(Math.random() * JOURNAL_PROMPTS.length)])}
                className="text-[10px] text-[#84A98C] font-medium mt-2 ml-11 hover:underline"
                data-testid="shuffle-prompt-button"
              >
                Try a different prompt
              </button>
            </div>

            {/* Text Entry */}
            <div className="card-neuro p-6 mb-5">
              <Textarea
                data-testid="journal-entry-textarea"
                value={journalEntry}
                onChange={(e) => setJournalEntry(e.target.value)}
                placeholder="Let your thoughts flow freely..."
                className="border-[#E1D9CD] bg-[#FAF7F2] rounded-xl resize-none min-h-[140px] focus:border-[#2D6A4F] text-[#1A2421] leading-relaxed"
              />
              <p className="text-[10px] text-[#78857F] mt-2 text-right">
                {journalEntry.length} characters
              </p>
            </div>

            {/* Mood After */}
            <div className="card-neuro p-6 mb-6">
              <p className="text-sm font-medium text-[#1A2421] mb-3">How are you feeling now?</p>
              <div className="flex flex-wrap gap-2">
                {MOOD_OPTIONS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`mood-after-${m.id}`}
                    onClick={() => setMoodAfter(m.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      moodAfter === m.id
                        ? "text-white shadow-sm"
                        : "bg-white border border-[#E1D9CD] text-[#78857F] hover:border-current"
                    }`}
                    style={moodAfter === m.id ? { backgroundColor: m.color } : {}}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={saveJournal}
                disabled={savingJournal || !journalEntry.trim()}
                data-testid="save-journal-button"
                className="flex-1 rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium transition-all hover:-translate-y-0.5 shadow-md"
              >
                {savingJournal ? (
                  <span className="flex items-center gap-2">
                    <Sparkle size={16} className="animate-spin" /> Saving & reflecting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Save Reflection <ArrowRight size={16} />
                  </span>
                )}
              </Button>
              <Button
                onClick={resetAll}
                variant="outline"
                data-testid="discard-journal-button"
                className="rounded-full border-[#E1D9CD] text-[#78857F] px-6"
              >
                Skip
              </Button>
            </div>
          </motion.div>
        )}

        {/* ── Saved Journal + AI Reflection ── */}
        {stage === "saved" && savedJournal && (
          <motion.div
            key="saved"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl mx-auto"
            data-testid="journal-saved-screen"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-16 h-16 rounded-full bg-[#84A98C]/15 flex items-center justify-center mx-auto mb-4"
              >
                <BookOpen size={28} weight="duotone" className="text-[#84A98C]" />
              </motion.div>
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#1A2421] mb-1">Reflection Saved</h2>
              <p className="text-sm text-[#78857F]">Your words have been captured.</p>
            </div>

            {/* Journal Entry Review */}
            <div className="card-neuro p-6 mb-4">
              <p className="text-xs text-[#84A98C] italic mb-2">"{savedJournal.prompt}"</p>
              <p className="text-sm text-[#1A2421] leading-relaxed">{savedJournal.entry}</p>
              {savedJournal.mood_after && (
                <p className="text-xs text-[#78857F] mt-2">
                  Post-session mood: <span className="capitalize font-medium text-[#2D6A4F]">{savedJournal.mood_after}</span>
                </p>
              )}
            </div>

            {/* AI Reflection — Premium */}
            {savedJournal.ai_reflection && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-neuro p-6 mb-6 relative overflow-hidden"
                data-testid="ai-reflection-card"
              >
                <div className="absolute top-0 right-0 w-20 h-20 rounded-full bg-[#CDB4DB] blur-[40px] opacity-15" />
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={16} weight="duotone" className="text-[#CDB4DB]" />
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#CDB4DB]">Coach KB's Reflection</p>
                </div>
                <p className="text-sm text-[#1A2421] leading-relaxed italic" data-testid="ai-reflection-text">
                  {savedJournal.ai_reflection}
                </p>
              </motion.div>
            )}

            {!savedJournal.ai_reflection && !isPremium && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-neuro p-6 mb-6 text-center relative overflow-hidden"
                data-testid="premium-upsell-journal"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#F4A261]/5" />
                <Sparkle size={24} weight="duotone" className="text-[#F4A261] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#1A2421] mb-1">Unlock Coach KB's AI Reflection</p>
                <p className="text-xs text-[#78857F] mb-3">
                  Premium members get a personalized insight from Coach KB after every journal entry.
                </p>
                <PremiumBadge onClick={() => setShowPremiumModal(true)} />
              </motion.div>
            )}

            {!savedJournal.ai_reflection && isPremium && (
              <div className="card-neuro p-5 mb-6 text-center">
                <Leaf size={20} weight="duotone" className="text-[#84A98C] mx-auto mb-2" />
                <p className="text-sm text-[#84A98C] italic">"Every reflection is a step toward deeper self-awareness."</p>
              </div>
            )}

            <Button
              onClick={resetAll}
              data-testid="journal-done-button"
              className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium transition-all hover:-translate-y-0.5 shadow-md"
            >
              Return to Sessions
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <PremiumUpgrade
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />
    </div>
  );
}
